import axios from 'axios';

const api = axios.create({
    baseURL: 'https://talentos-services.xn--am-yja.org/api',
    timeout: 30000,
    headers: { 
        'Content-Type': 'application/json'
    }
});

// Interceptor para agregar API Key y JWT Token a todas las peticiones
api.interceptors.request.use((config) => {
    // Usar la API Key de la variable de entorno o la key por defecto
    const apiKey = process.env.REACT_APP_TALENTOS_API_KEY || 'tal_internal_practices_service_5604f8e446adefc0876b5e827760cb547cb531b9787e6bc73d3af39595174897';
    
    if (apiKey) {
        config.headers['X-API-Key'] = apiKey;
    } else {
        console.warn('⚠️ No API Key found!');
    }
    
    // Agregar JWT Token si está disponible (necesario para algunos endpoints como applications)
    const jwtToken = localStorage.getItem('atfgscomm');
    if (jwtToken) {
        config.headers['Authorization'] = `Bearer ${jwtToken}`;
    }
    
    return config;
}, (error) => {
    return Promise.reject(error);
});

export default class TalentosAPI {
    // ==================== USUARIOS ====================
    
    static getUsers(filters = {}) {
        return new Promise((resolve, reject) => {
            api.get('/users', { params: filters })
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static getUserById(userId) {
        return new Promise((resolve, reject) => {
            api.get(`/users/${userId}`)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static getPostulants(filters = {}) {
        return new Promise((resolve, reject) => {
            api.get('/users/all/postulants', { params: filters })
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static getEmployers(filters = {}) {
        return new Promise((resolve, reject) => {
            api.get('/users/all/employers', { params: filters })
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static getTeachers(filters = {}) {
        return new Promise((resolve, reject) => {
            api.get('/users/all/teachers', { params: filters })
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static getUsersByCountry(country) {
        return new Promise((resolve, reject) => {
            api.get(`/users/by/country/${country}`)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static getNearbyPostulants(lat, lng, distance = 10) {
        return new Promise((resolve, reject) => {
            api.get('/users/nearby/postulants', { 
                params: { lat, lng, distance } 
            })
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static updateUser(userId, data) {
        return new Promise((resolve, reject) => {
            api.put(`/users/${userId}`, data)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static deleteUser(userId) {
        return new Promise((resolve, reject) => {
            api.delete(`/users/${userId}`)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static registerPostulant(data) {
        return new Promise((resolve, reject) => {
            api.post('/users/register/postulant', data)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static registerEmployer(data) {
        return new Promise((resolve, reject) => {
            api.post('/users/register/employer', data)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static syncPostulant(data) {
        return new Promise((resolve, reject) => {
            api.post('/users/sync/postulant', data)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static getUserCredits(userId) {
        return new Promise((resolve, reject) => {
            api.get(`/users/credits/${userId}`)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    // ==================== TRABAJOS (JOBS) ====================

    static getJobs(filters = {}) {
        return new Promise((resolve, reject) => {
            console.log('API getJobs called with filters:', filters);
            console.log('API Base URL:', api.defaults.baseURL);
            console.log('API Headers:', api.defaults.headers);
            
            api.get('/jobs', { params: filters })
                .then(res => {
                    console.log('API Jobs Full Response:', res);
                    console.log('API Jobs Response Data:', res.data);
                    console.log('API Jobs Response Status:', res.status);
                    console.log('API Jobs Response Headers:', res.headers);
                    
                    // Intentar diferentes formatos de respuesta
                    let jobsData = res.data;
                    if (res.data && typeof res.data === 'object') {
                        if (Array.isArray(res.data)) {
                            jobsData = res.data;
                        } else if (Array.isArray(res.data.data)) {
                            jobsData = res.data.data;
                        } else if (Array.isArray(res.data.jobs)) {
                            jobsData = res.data.jobs;
                        } else if (res.data.results && Array.isArray(res.data.results)) {
                            jobsData = res.data.results;
                        }
                    }
                    
                    console.log('API Jobs Processed Data:', jobsData);
                    resolve(jobsData);
                })
                .catch(err => {
                    console.error('API Jobs Error Full:', err);
                    console.error('API Jobs Error Response:', err.response);
                    console.error('API Jobs Error Message:', err.message);
                    reject(err);
                })
        });
    }

    static getJobById(jobId) {
        return new Promise((resolve, reject) => {
            api.get(`/jobs/${jobId}`)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static getJobCount(filters = {}) {
        return new Promise((resolve, reject) => {
            api.get('/jobs/count', { params: filters })
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static getNearbyJobs(lat, lng, distance = 10) {
        return new Promise((resolve, reject) => {
            api.get('/jobs/nearby', { 
                params: { lat, lng, distance } 
            })
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static getJobsByCountry(country) {
        return new Promise((resolve, reject) => {
            api.get(`/jobs/country/${country}`)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static getJobsByCompany(companyId) {
        return new Promise((resolve, reject) => {
            api.get(`/jobs/company/${companyId}`)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static createJob(data) {
        return new Promise((resolve, reject) => {
            api.post('/jobs', data)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static updateJob(jobId, data) {
        return new Promise((resolve, reject) => {
            api.put(`/jobs/${jobId}`, data)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static deleteJob(jobId) {
        return new Promise((resolve, reject) => {
            api.delete(`/jobs/${jobId}`)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    // ==================== PRÁCTICAS (PRACTICES) ====================

    static getPractices(filters = {}) {
        return new Promise((resolve, reject) => {
            api.get('/practices', { params: filters })
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static getPracticeById(practiceId) {
        return new Promise((resolve, reject) => {
            api.get(`/practices/${practiceId}`)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static getPracticeCount(filters = {}) {
        return new Promise((resolve, reject) => {
            api.get('/practices/count', { params: filters })
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static getNearbyPractices(lat, lng, distance = 10) {
        return new Promise((resolve, reject) => {
            api.get('/practices/nearby', { 
                params: { lat, lng, distance } 
            })
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static getPracticesByCountry(country) {
        return new Promise((resolve, reject) => {
            api.get(`/practices/country/${country}`)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static getPracticesByCompany(companyId) {
        return new Promise((resolve, reject) => {
            api.get(`/practices/company/${companyId}`)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static getPracticesByEmployer(keycloakId) {
        return new Promise((resolve, reject) => {
            api.get(`/practices/employer/keycloak/${keycloakId}`)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static createPractice(data) {
        return new Promise((resolve, reject) => {
            api.post('/practices', data)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static updatePractice(practiceId, data) {
        return new Promise((resolve, reject) => {
            api.put(`/practices/${practiceId}`, data)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static deletePractice(practiceId) {
        return new Promise((resolve, reject) => {
            api.delete(`/practices/${practiceId}`)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static importPractices(file) {
        return new Promise((resolve, reject) => {
            const formData = new FormData();
            formData.append('file', file);
            api.post('/practices/import', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            })
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    // ==================== POSTULACIONES (APPLICATIONS) ====================

    static getApplications(filters = {}) {
        return new Promise((resolve, reject) => {
            api.get('/applications', { params: filters })
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static getApplicationById(applicationId) {
        return new Promise((resolve, reject) => {
            api.get(`/applications/${applicationId}`)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static getApplicationsByUser(userId, options = {}) {
        return new Promise((resolve, reject) => {
            api.get(`/applications/user/${userId}`, { params: options })
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static getApplicationsByUserKeycloak(keycloakId, options = {}) {
        return new Promise((resolve, reject) => {
            api.get(`/applications/user/keycloak/${keycloakId}`, { params: options })
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static getApplicationsByJob(jobId) {
        return new Promise((resolve, reject) => {
            api.get(`/applications/job/${jobId}`)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static getApplicationsByPractice(practiceId) {
        return new Promise((resolve, reject) => {
            api.get(`/applications/practice/${practiceId}`)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static getApplicationsByEmployer(keycloakId) {
        return new Promise((resolve, reject) => {
            api.get(`/applications/practice/employer/keycloak/${keycloakId}`)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static createApplication(data) {
        return new Promise((resolve, reject) => {
            api.post('/applications/apply', data)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static updateApplicationStage(applicationId, newStage) {
        return new Promise((resolve, reject) => {
            api.patch(`/applications/${applicationId}/stage`, { newStage })
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static updateApplicationStatus(applicationId, status) {
        return new Promise((resolve, reject) => {
            api.patch(`/applications/${applicationId}/status`, { status })
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static updateApplication(applicationId, data) {
        return new Promise((resolve, reject) => {
            api.put(`/applications/${applicationId}`, data)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static deleteApplication(applicationId) {
        return new Promise((resolve, reject) => {
            api.delete(`/applications/${applicationId}`)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    // ==================== COMPAÑÍAS (COMPANIES) ====================

    static getCompanies(filters = {}) {
        return new Promise((resolve, reject) => {
            api.get('/companies', { params: filters })
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static getCompanyById(companyId) {
        return new Promise((resolve, reject) => {
            api.get(`/companies/${companyId}`)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static getNearbyCompanies(lat, lng, distance = 10) {
        return new Promise((resolve, reject) => {
            api.get('/companies/nearby', { 
                params: { lat, lng, distance } 
            })
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static createCompany(data) {
        return new Promise((resolve, reject) => {
            api.post('/companies', data)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static updateCompany(companyId, data) {
        return new Promise((resolve, reject) => {
            api.put(`/companies/${companyId}`, data)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static deleteCompany(companyId) {
        return new Promise((resolve, reject) => {
            api.delete(`/companies/${companyId}`)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    // ==================== INSTITUCIONES (INSTITUTIONS) ====================

    static getInstitutions(filters = {}) {
        return new Promise((resolve, reject) => {
            api.get('/institutions', { params: filters })
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static getInstitutionById(institutionId) {
        return new Promise((resolve, reject) => {
            api.get(`/institutions/${institutionId}`)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static getNearbyInstitutions(lat, lng, distance = 10) {
        return new Promise((resolve, reject) => {
            api.get('/institutions/nearby', { 
                params: { lat, lng, distance } 
            })
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static createInstitution(data) {
        return new Promise((resolve, reject) => {
            api.post('/institutions', data)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static updateInstitution(institutionId, data) {
        return new Promise((resolve, reject) => {
            api.put(`/institutions/${institutionId}`, data)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static deleteInstitution(institutionId) {
        return new Promise((resolve, reject) => {
            api.delete(`/institutions/${institutionId}`)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    // ==================== CONSULTAS DE PRÁCTICAS ====================

    static getPracticeApplications(filters = {}) {
        return new Promise((resolve, reject) => {
            api.get('/practices-query/applications', { params: filters })
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static getPracticeDetails(practiceId) {
        return new Promise((resolve, reject) => {
            api.get(`/practices-query/practice/${practiceId}`)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static getEmployerPractices(employerId, filters = {}) {
        return new Promise((resolve, reject) => {
            api.get(`/practices-query/employer/${employerId}/practices`, { params: filters })
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static getEmployerTerms(employerId) {
        return new Promise((resolve, reject) => {
            api.get(`/practices-query/employer/${employerId}/terms`)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static getApplicationInfo(applicationId) {
        return new Promise((resolve, reject) => {
            api.get(`/practices-query/application/${applicationId}`)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    // ==================== PLANES Y PAGOS ====================

    static getPlans() {
        return new Promise((resolve, reject) => {
            api.get('/plan')
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static getActivePlans() {
        return new Promise((resolve, reject) => {
            api.get('/plan/active')
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static getPlanById(planId) {
        return new Promise((resolve, reject) => {
            api.get(`/plan/${planId}`)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static createPayment(data) {
        return new Promise((resolve, reject) => {
            api.post('/payments/create', data)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static getBillings(userId) {
        return new Promise((resolve, reject) => {
            api.get(`/payments/billings/${userId}`)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    // ==================== IMPORTACIÓN MASIVA ====================

    static importPostulants(file) {
        return new Promise((resolve, reject) => {
            const formData = new FormData();
            formData.append('file', file);
            api.post('/import/postulants', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            })
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static importEmployers(file) {
        return new Promise((resolve, reject) => {
            const formData = new FormData();
            formData.append('file', file);
            api.post('/import/employers', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            })
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static importTeachers(file) {
        return new Promise((resolve, reject) => {
            const formData = new FormData();
            formData.append('file', file);
            api.post('/import/teachers', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            })
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static importJobs(file) {
        return new Promise((resolve, reject) => {
            const formData = new FormData();
            formData.append('file', file);
            api.post('/import/jobs', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            })
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    // ==================== WEBHOOKS ====================

    static registerWebhook(data) {
        return new Promise((resolve, reject) => {
            api.post('/webhooks/config', data)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static getWebhooks() {
        return new Promise((resolve, reject) => {
            api.get('/webhooks/config')
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static updateWebhook(webhookId, data) {
        return new Promise((resolve, reject) => {
            api.put(`/webhooks/config/${webhookId}`, data)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static toggleWebhook(webhookId) {
        return new Promise((resolve, reject) => {
            api.patch(`/webhooks/config/${webhookId}/toggle`)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static deleteWebhook(webhookId) {
        return new Promise((resolve, reject) => {
            api.delete(`/webhooks/config/${webhookId}`)
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static getWebhookLogs(filters = {}) {
        return new Promise((resolve, reject) => {
            api.get('/webhooks/config/logs', { params: filters })
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    // ==================== DASHBOARD Y KPIs ====================

    static getKPIs(filters = {}) {
        return new Promise((resolve, reject) => {
            api.get('/dashboard/kpis', { params: filters })
                .then(res => {
                    console.log('API KPIs Response:', res.data);
                    resolve(res.data);
                })
                .catch(err => {
                    console.error('API KPIs Error:', err);
                    reject(err);
                })
        });
    }

    static getGrowthStatistics(period = 'month', filters = {}) {
        return new Promise((resolve, reject) => {
            api.get('/dashboard/growth', { 
                params: { period, ...filters } 
            })
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static getCategoryStatistics(filters = {}) {
        return new Promise((resolve, reject) => {
            api.get('/dashboard/categories', { params: filters })
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static getGeographicStatistics(filters = {}) {
        return new Promise((resolve, reject) => {
            api.get('/dashboard/geography', { params: filters })
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static getApplicationStatistics(filters = {}) {
        return new Promise((resolve, reject) => {
            api.get('/dashboard/applications', { params: filters })
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static getPaymentStatistics(filters = {}) {
        return new Promise((resolve, reject) => {
            api.get('/dashboard/payments', { params: filters })
                .then(res => resolve(res.data))
                .catch(err => reject(err))
        });
    }

    static getDashboardSummary(filters = {}) {
        return new Promise((resolve, reject) => {
            api.get('/dashboard/summary', { params: filters })
                .then(res => {
                    console.log('API Dashboard Summary Response:', res.data);
                    resolve(res.data);
                })
                .catch(err => {
                    console.error('API Dashboard Summary Error:', err);
                    reject(err);
                })
        });
    }
}

